import { io, Socket } from 'socket.io-client';
import { WebSocketMessage, BuildJob, Deployment } from '@/types';
import { authService } from './auth';

type MessageHandler = (message: WebSocketMessage) => void;
type ConnectionHandler = (connected: boolean) => void;

class WebSocketService {
  private static instance: WebSocketService;
  private socket: Socket | null = null;
  private messageHandlers: Map<string, Set<MessageHandler>> = new Map();
  private connectionHandlers: Set<ConnectionHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isIntentionalDisconnect = false;

  private constructor() {}

  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    const token = authService.getToken();
    if (!token) {
      console.warn('No auth token available, skipping WebSocket connection');
      return;
    }

    this.isIntentionalDisconnect = false;
    
    this.socket = io(import.meta.env.VITE_WS_URL || 'ws://localhost:8000', {
      auth: {
        token,
      },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
    });

    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    // Remove all existing listeners to prevent duplicates on reconnect
    this.socket.removeAllListeners();

    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      this.notifyConnectionHandlers(true);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      
      if (!this.isIntentionalDisconnect) {
        this.notifyConnectionHandlers(false);
        
        if (reason === 'io server disconnect') {
          // Server disconnected us, try to reconnect
          setTimeout(() => this.connect(), this.reconnectDelay);
        }
      }
    });

    this.socket.on('error', (error) => {
      console.error('WebSocket error:', error);
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      this.reconnectAttempts = attemptNumber;
      console.log(`WebSocket reconnection attempt ${attemptNumber}`);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('WebSocket reconnection failed');
      this.notifyConnectionHandlers(false);
    });

    // Application-specific message handlers
    this.socket.on('message', (message: WebSocketMessage) => {
      this.handleMessage(message);
    });

    this.socket.on('build:update', (job: BuildJob) => {
      this.handleMessage({
        type: 'build',
        applicationId: job.applicationId,
        jobId: job.id,
        data: job,
        timestamp: new Date().toISOString(),
      });
    });

    this.socket.on('deployment:update', (deployment: Deployment) => {
      this.handleMessage({
        type: 'deployment',
        applicationId: deployment.applicationId,
        data: deployment,
        timestamp: new Date().toISOString(),
      });
    });

    this.socket.on('log', (data: { applicationId: string; message: string }) => {
      this.handleMessage({
        type: 'log',
        applicationId: data.applicationId,
        data: data.message,
        timestamp: new Date().toISOString(),
      });
    });
  }

  disconnect(): void {
    this.isIntentionalDisconnect = true;
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.notifyConnectionHandlers(false);
  }

  private handleMessage(message: WebSocketMessage): void {
    // Global handlers
    const globalHandlers = this.messageHandlers.get('*');
    if (globalHandlers) {
      globalHandlers.forEach(handler => handler(message));
    }

    // Type-specific handlers
    const typeHandlers = this.messageHandlers.get(message.type);
    if (typeHandlers) {
      typeHandlers.forEach(handler => handler(message));
    }

    // Application-specific handlers
    if (message.applicationId) {
      const appHandlers = this.messageHandlers.get(`app:${message.applicationId}`);
      if (appHandlers) {
        appHandlers.forEach(handler => handler(message));
      }
    }

    // Job-specific handlers
    if (message.jobId) {
      const jobHandlers = this.messageHandlers.get(`job:${message.jobId}`);
      if (jobHandlers) {
        jobHandlers.forEach(handler => handler(message));
      }
    }
  }

  onMessage(key: string, handler: MessageHandler): () => void {
    if (!this.messageHandlers.has(key)) {
      this.messageHandlers.set(key, new Set());
    }
    
    this.messageHandlers.get(key)!.add(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.messageHandlers.get(key);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.messageHandlers.delete(key);
        }
      }
    };
  }

  onConnection(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler);
    
    // Immediately notify current connection status
    handler(this.isConnected());

    // Return unsubscribe function
    return () => {
      this.connectionHandlers.delete(handler);
    };
  }

  private notifyConnectionHandlers(connected: boolean): void {
    this.connectionHandlers.forEach(handler => handler(connected));
  }

  emit(event: string, data: any): void {
    if (!this.socket?.connected) {
      console.warn('WebSocket not connected, cannot emit event:', event);
      return;
    }

    this.socket.emit(event, data);
  }

  on(event: string, handler: (...args: any[]) => void): void {
    this.socket?.on(event, handler);
  }

  off(event: string, handler: (...args: any[]) => void): void {
    this.socket?.off(event, handler);
  }

  // Application-specific methods
  subscribeToApplication(applicationId: string): void {
    this.emit('subscribe:application', { applicationId });
  }

  unsubscribeFromApplication(applicationId: string): void {
    this.emit('unsubscribe:application', { applicationId });
  }

  subscribeToBuildJob(jobId: string): void {
    this.emit('subscribe:job', { jobId });
  }

  unsubscribeFromBuildJob(jobId: string): void {
    this.emit('unsubscribe:job', { jobId });
  }

  subscribeToDeployment(deploymentId: string): void {
    this.emit('subscribe:deployment', { deploymentId });
  }

  unsubscribeFromDeployment(deploymentId: string): void {
    this.emit('unsubscribe:deployment', { deploymentId });
  }

  // Status methods
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  // Manual reconnect
  reconnect(): void {
    if (!this.isConnected()) {
      this.connect();
    }
  }
}

export const websocketService = WebSocketService.getInstance();